import {
    Box,
    FormControl,
    FormLabel,
    Stack,
    Text,
    VStack,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { Field } from 'formik';
import { useState, useMemo } from 'react';

import { Input } from 'components/v4';
import FieldError from 'components/forms/new/FieldError';
import FieldToolTip from 'components/forms/new/FieldToolTip';
import {
    descriptionStyling,
    dropDownFormStyle,
    formSelectStyles,
    formLabelStyling,
    stackSpacing,
    stackStyling,
    textBoxStyling,
} from 'components/forms/new/formStyles';
import { CustomRegionOption } from 'pages/ga/trs-clusters/formFields/CustomRegionOption';

const TRSClusterRegionField = ({
    errors,
    values,
    touched,
    regions = [],
    darkMode,
    mode = 'Add',
    clusterData,
    usedRegionIds = new Set(), // Set of region IDs that already have clusters
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const isAddPage = useMemo(() => mode === 'Add', [mode]);

    const selectedRegion = useMemo(() => {
        if (!regions.length || !values?.regionID) return null;
        return regions.find((r) => r.id === values.regionID) || null;
    }, [regions, values?.regionID]);

    const currentRegionName = useMemo(() => {
        if (!regions.length || !clusterData?.regionID) return '';
        const region = regions.find((r) => r.id === clusterData.regionID);
        return region?.name || '';
    }, [regions, clusterData?.regionID]);

    const enhancedRegions = useMemo(() => {
        if (!regions.length) return [];

        return regions.map((region) => {
            const isUsed = usedRegionIds.has(region.id);
            return {
                ...region,
                isDisabled: isUsed,
                // Add visual indicator for used regions
                label: isUsed
                    ? `${region.name} (Already had a cluster)`
                    : region.name,
            };
        });
    }, [regions, usedRegionIds]);

    const baseStyles = dropDownFormStyle(darkMode);
    const filterOption = (option, inputValue) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase());
    const isOptionDisabled = (option) => {
        return option.isDisabled;
    };

    return (
        <FormControl
            isRequired={isAddPage}
            sx={{ margin: '0 !important' }}
            isInvalid={!!errors.region && touched.region}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}>
            <Stack
                direction="row"
                spacing={stackSpacing}
                sx={stackStyling(true)}>
                <Box>
                    <FormLabel sx={formLabelStyling(darkMode)}>
                        Region{' '}
                        {!isAddPage && (
                            <span className="font-medium text-gray-400">
                                (read-only)
                            </span>
                        )}
                    </FormLabel>
                    <Text
                        sx={{
                            ...descriptionStyling,
                            color: darkMode ? 'gray.100' : '#757575',
                        }}
                        maxW={250}>
                        Choose from TCAP defined Regions
                    </Text>
                </Box>
                <Field name="regionID">
                    {({ field, form: { setFieldValue, setTouched } }) => (
                        <Box
                            flex="1"
                            sx={{
                                paddingTop: '15px',
                                ...formSelectStyles,
                            }}>
                            <VStack width="100%" alignItems="normal">
                                {!isAddPage ? (
                                    <Input
                                        className="!h-[30px] cursor-default rounded-[5px] pl-2 !text-gray-400"
                                        value={currentRegionName}
                                        readOnly
                                    />
                                ) : (
                                    <Select
                                        {...field}
                                        tabSelectsValue
                                        openMenuOnFocus
                                        placeholder="Select region"
                                        sx={{
                                            ...textBoxStyling,
                                            color: darkMode
                                                ? 'dark.textDark'
                                                : null,
                                            background: darkMode
                                                ? 'dark.bgDark'
                                                : null,
                                            borderColor: darkMode
                                                ? 'dark.tcap-borderGray'
                                                : null,
                                        }}
                                        value={selectedRegion}
                                        options={enhancedRegions}
                                        getOptionLabel={(option) =>
                                            option.label || option.name
                                        }
                                        getOptionValue={(option) => option.id}
                                        isOptionDisabled={isOptionDisabled}
                                        filterOption={filterOption}
                                        selectedOptionColor="brand"
                                        chakraStyles={baseStyles}
                                        onChange={(selectedOption) => {
                                            if (selectedOption?.isDisabled) {
                                                return;
                                            }

                                            const regionId =
                                                selectedOption?.id || '';
                                            setFieldValue('regionID', regionId);
                                        }}
                                        onBlur={() => {
                                            setTouched({
                                                ...touched,
                                                regionID: true,
                                            });
                                        }}
                                        // Add custom menu behavior to show tooltips or additional info
                                        components={{
                                            Option: (props) => (
                                                <CustomRegionOption
                                                    darkMode={darkMode}
                                                    {...props}
                                                />
                                            ),
                                        }}
                                    />
                                )}
                            </VStack>
                        </Box>
                    )}
                </Field>

                {isHovered && (
                    <FieldToolTip
                        label="Select the geographical region for this cluster. Regions with existing clusters are disabled."
                        ariaLabel="cluster-region-tip"
                        left="25px"
                        top="25px"
                    />
                )}
            </Stack>
            {isAddPage && (
                <FieldError
                    error={errors?.regionID}
                    touched={touched?.regionID}
                />
            )}
        </FormControl>
    );
};

export default TRSClusterRegionField;
